import React, { useState, useEffect } from "react";
import { useBranch } from '../../../layouts/BranchContext';
import {
  Button,
  Box,
  Tooltip,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
  useTheme,
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Modal,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

const Alert = React.forwardRef(function Alert(props, ref) {
  return (
    <MuiAlert
      elevation={6}
      ref={ref}
      variant="filled"
      {...props}
    />
  );
});

const AddUser = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const apiUrl =
    process.env.REACT_APP_API_BASE_URL ||
    "https://wims.revisetoexcel.com";

  // =========================================================
  // STATES
  // =========================================================

  const [showSuccess, setShowSuccess] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState(false);

  const [passwordMatchError, setPasswordMatchError] =
    useState(false);

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] =
    useState("success");

  const [roleList, setRoleList] = useState([]);

  // Original employees received from API
  const [allEmployeeList, setAllEmployeeList] =
    useState([]);

  // Employees filtered according to role
  const [employeeList, setEmployeeList] =
    useState([]);

  // Students
  const [studentList, setStudentList] = useState([]);

  // Branches
  const { selectedBranchId, branchList, isMainBranchAdmin } = useBranch();

  const [loading, setLoading] = useState(false);

  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    branchId: selectedBranchId || "", // Use global branch ID
    roleId: "",
    employeeId: "",
    userName: "",
    password: "",
    confirmPassword: "",
    expiryDate: "",
  });

  // =========================================================
  // HELPER
  // =========================================================

  const showMessage = (
    message,
    severity = "error"
  ) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const getUserFromSession = () => {
    try {
      const storedUser =
        sessionStorage.getItem("user");

      if (!storedUser) {
        return {};
      }

      return JSON.parse(storedUser) || {};
    } catch (error) {
      console.error(
        "Invalid user object in sessionStorage:",
        error
      );

      return {};
    }
  };

  // =========================================================
  // SELECTED ROLE
  // =========================================================

  const selectedRole = roleList.find(
    (role) =>
      String(role.id) ===
      String(formData.roleId)
  );

  const selectedRoleName = String(
    selectedRole?.name ||
      selectedRole?.userGroupName ||
      selectedRole?.role_name ||
      selectedRole?.roleName ||
      ""
  )
    .trim()
    .toLowerCase();

  const isStudentRole =
    selectedRoleName === "student";



  // =========================================================
  // LOAD ROLES + EMPLOYEES
  // =========================================================

  const loadData = async (branchId = selectedBranchId) => {
    if (!branchId) {
      return;
    }

    setLoading(true);

    const accessToken =
      localStorage.getItem(
        "accessToken"
      );

    if (!accessToken) {
      showMessage(
        "Authentication token missing"
      );

      setLoading(false);
      return;
    }

    const headers = {
      Authorization:
        `Bearer ${accessToken}`,
    };

    const user =
      getUserFromSession();

    // =====================================================
    // ORGANIZATION ID
    // =====================================================

    let organizationId =
      user?.organization_id ||
      user?.organization ||
      "";

    // =====================================================
    // SELECTED BRANCH
    // =====================================================

    const selectedBranch =
      branchList.find(
        (branch) =>
          String(branch.id) ===
          String(branchId)
      );

    if (
      !organizationId &&
      selectedBranch
    ) {
      organizationId =
        selectedBranch.organization ||
        "";
    }

    console.log(
      "======================================"
    );

    console.log(
      "========== LOAD DATA =========="
    );

    console.log(
      "Selected Branch ID:",
      branchId
    );

    console.log(
      "Organization ID:",
      organizationId
    );

    console.log(
      "Selected Branch:",
      selectedBranch
    );

    // =====================================================
    // QUERY PARAMETERS
    // =====================================================

    const params =
      new URLSearchParams();

    if (organizationId) {
      params.append(
        "organization_id",
        organizationId
      );
    }

    if (branchId) {
      params.append(
        "branch_id",
        branchId
      );
    }

    const queryString =
      params.toString()
        ? `?${params.toString()}`
        : "";

    // =====================================================
    // API URLS
    // =====================================================

    const rolesUrl =
      `${apiUrl}/api/staff/user-roles/${queryString}`;

    const employeesUrl =
      `${apiUrl}/api/staff/employees/filter/${queryString}`;

    console.log(
      "ROLES API:",
      rolesUrl
    );

    console.log(
      "EMPLOYEES FILTER API:",
      employeesUrl
    );

    try {
      const [
        rolesResponse,
        employeesResponse,
      ] = await Promise.all([
        fetch(
          rolesUrl,
          {
            method: "GET",
            headers,
          }
        ),

        fetch(
          employeesUrl,
          {
            method: "GET",
            headers,
          }
        ),
      ]);

      // =====================================================
      // ROLES
      // =====================================================

      if (
        rolesResponse.ok
      ) {
        const rolesData =
          await rolesResponse.json();

        console.log(
          "========== ROLES RESPONSE =========="
        );

        console.log(
          rolesData
        );

        const roles =
          Array.isArray(
            rolesData
          )
            ? rolesData
            : rolesData?.results ||
              [];

        const normalizedRoles =
          roles.map(
            (role) => ({
              ...role,

              id:
                role.id ??
                role.role_id ??
                role.roleId,

              name:
                role.name ||
                role.userGroupName ||
                role.role_name ||
                role.roleName ||
                "",
            })
          );

        console.log(
          "Normalized Roles:",
          normalizedRoles
        );

        setRoleList(
          normalizedRoles
        );
      } else {
        const roleError =
          await rolesResponse
            .json()
            .catch(
              () => ({})
            );

        console.error(
          "Roles API error:",
          roleError
        );

        showMessage(
          "Failed to load roles"
        );
      }

      // =====================================================
      // EMPLOYEES
      // =====================================================

      if (
        employeesResponse.ok
      ) {
        const employeesData =
          await employeesResponse.json();

        console.log(
          "========== EMPLOYEE RESPONSE =========="
        );

        console.log(
          employeesData
        );

        const employees =
          Array.isArray(
            employeesData
          )
            ? employeesData
            : employeesData?.results ||
              [];

        console.log(
          "ALL EMPLOYEES FROM API:",
          employees
        );

        // Keep original API list
        setAllEmployeeList(
          employees
        );

        // Initially show all employees
        setEmployeeList(
          employees
        );
      } else {
        const employeeError =
          await employeesResponse
            .json()
            .catch(
              () => ({})
            );

        console.error(
          "Employees API error:",
          employeeError
        );

        showMessage(
          employeeError?.organization_id?.[0] ||
            employeeError?.detail ||
            "Failed to load employees"
        );
      }
    } catch (error) {
      console.error(
        "Error loading roles/employees:",
        error
      );

      showMessage(
        "Error loading data"
      );
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // LOAD STUDENTS
  // =========================================================

  const loadStudents = async () => {
  const accessToken = localStorage.getItem("accessToken");
  if (!accessToken || !selectedBranchId) {
    return;
  }
  const headers = { Authorization: `Bearer ${accessToken}` };
  const params = new URLSearchParams();
  params.append("branch_id", selectedBranchId);

    const url =
      `${apiUrl}/api/admission/admissions/?${params.toString()}`;

    console.log(
      "STUDENTS API:",
      url
    );

    try {
      const response =
        await fetch(
          url,
          {
            method: "GET",
            headers,
          }
        );

      if (response.ok) {
        const data =
          await response.json();

        console.log(
          "Students response:",
          data
        );

        const students =
          Array.isArray(data)
            ? data
            : data?.results ||
              [];

        setStudentList(
          students
        );
      } else {
        const errorData =
          await response
            .json()
            .catch(
              () => ({})
            );

        console.error(
          "Students API error:",
          errorData
        );

        showMessage(
          "Failed to load students"
        );
      }
    } catch (error) {
      console.error(
        "Error loading students:",
        error
      );

      showMessage(
        "Error loading students"
      );
    }
  };

 

  // =========================================================
  // BRANCH CHANGE
  // =========================================================

  useEffect(() => {
  if (!selectedBranchId) {
    return;
  }
  // Reset dependent fields
  setFormData((prev) => ({
    ...prev,
    branchId: selectedBranchId, // Update branchId in formData
    roleId: "",
    employeeId: "",
  }));
  setRoleList([]);
  setAllEmployeeList([]);
  setEmployeeList([]);
  setStudentList([]);
  loadData(selectedBranchId);
}, [selectedBranchId]);

  // =========================================================
  // ROLE CHANGE / FILTER EMPLOYEES
  // =========================================================

  useEffect(() => {
    if (!formData.roleId) {
      setEmployeeList([]);
      setStudentList([]);
      return;
    }

    console.log(
      "======================================"
    );

    console.log(
      "ROLE CHANGED"
    );

    console.log(
      "Selected Role:",
      selectedRole
    );

    console.log(
      "Selected Role Name:",
      selectedRoleName
    );

    // =====================================================
    // STUDENT ROLE
    // =====================================================

    if (isStudentRole) {
      console.log(
        "Selected Student role"
      );

      setEmployeeList([]);

      setStudentList([]);

      loadStudents();

      return;
    }

    // =====================================================
    // EMPLOYEE ROLE
    // =====================================================

    const filteredEmployees =
      allEmployeeList.filter(
        (employee) => {
          const designationName =
            String(
              employee.designation_name ||
                employee.designationName ||
                employee.designation?.name ||
                employee.designation?.designation_name ||
                ""
            )
              .trim()
              .toLowerCase();

          console.log(
            "Employee:",
            employee.name ||
              employee.full_name
          );

          console.log(
            "Designation:",
            designationName
          );

          console.log(
            "Selected Role:",
            selectedRoleName
          );

          return (
            designationName ===
            selectedRoleName
          );
        }
      );

    console.log(
      "========== FILTERED EMPLOYEES =========="
    );

    console.log(
      filteredEmployees
    );

    setEmployeeList(
      filteredEmployees
    );
  }, [
    formData.roleId,
    allEmployeeList,
    selectedRoleName,
    isStudentRole,
  ]);

  // =========================================================
  // HANDLE CHANGE
  // =========================================================

  const handleChange = (event) => {
  const { name, value } = event.target;
  if (name === "branchId") {
    return; // Ignore branch changes (handled globally)
  }
  setFormData((prev) => ({
    ...prev,
    [name]: value,
  }));
  if (name === "password" || name === "confirmPassword") {
    setPasswordMatchError(false);
  }
};

  // =========================================================
  // PASSWORD
  // =========================================================

  const handleClickShowPassword =
    () => {
      setShowPassword(
        (prev) => !prev
      );
    };

  const handleClickShowConfirmPassword =
    () => {
      setShowConfirmPassword(
        (prev) => !prev
      );
    };

  // =========================================================
  // SAVE USER
  // =========================================================

  const handleSave = async (
    event
  ) => {
    if (
      event &&
      event.preventDefault
    ) {
      event.preventDefault();
    }

    // =====================================================
    // VALIDATION
    // =====================================================

    if (
      !formData.branchId ||
      !formData.roleId ||
      !formData.employeeId ||
      !formData.userName ||
      !formData.password ||
      !formData.confirmPassword ||
      !formData.expiryDate
    ) {
      showMessage(
        "Please fill all required fields!"
      );

      return;
    }

    if (
      formData.password !==
      formData.confirmPassword
    ) {
      setPasswordMatchError(
        true
      );

      return;
    }

    setSaving(true);

    const accessToken =
      localStorage.getItem(
        "accessToken"
      );

    if (!accessToken) {
      showMessage(
        "Authentication token missing"
      );

      setSaving(false);

      return;
    }

    // =====================================================
    // PAYLOAD
    // =====================================================

    const payload = {
      role: parseInt(
        formData.roleId,
        10
      ),

      ...(isStudentRole
        ? {
            admission:
              parseInt(
                formData.employeeId,
                10
              ),
          }
        : {
            employee:
              parseInt(
                formData.employeeId,
                10
              ),
          }),

      username:
        formData.userName,

      password:
        formData.password,

      confirm_password:
        formData.confirmPassword,

      expiry_date:
        formData.expiryDate,

     branch: parseInt(selectedBranchId, 10),
    };

    console.log(
      "======================================"
    );

    console.log(
      "CREATE USER PAYLOAD:",
      payload
    );

    try {
      const response =
        await fetch(
          `${apiUrl}/api/staff/users/`,
          {
            method: "POST",

            headers: {
              Authorization:
                `Bearer ${accessToken}`,

              "Content-Type":
                "application/json",
            },

            body: JSON.stringify(
              payload
            ),
          }
        );

      if (response.ok) {
        setShowSuccess(
          true
        );

        setSaving(false);

        return;
      }

      const errorData =
        await response
          .json()
          .catch(
            () => ({})
          );

      console.error(
        "Create user error:",
        errorData
      );

      let errorMessage =
        "Failed to add user";

      if (
        errorData.detail
      ) {
        errorMessage =
          errorData.detail;
      } else if (
        errorData.message
      ) {
        errorMessage =
          errorData.message;
      } else if (
        errorData.non_field_errors
      ) {
        errorMessage =
          Array.isArray(
            errorData.non_field_errors
          )
            ? errorData
                .non_field_errors[0]
            : errorData
                .non_field_errors;
      } else {
        const firstErrorField =
          Object.keys(
            errorData
          )[0];

        if (firstErrorField) {
          const fieldError =
            errorData[
              firstErrorField
            ];

          errorMessage =
            Array.isArray(
              fieldError
            )
              ? `${firstErrorField}: ${fieldError[0]}`
              : `${firstErrorField}: ${fieldError}`;
        }
      }

      showMessage(
        errorMessage
      );
    } catch (error) {
      console.error(
        "Error adding user:",
        error
      );

      showMessage(
        "Network error. Please check your connection and try again."
      );
    } finally {
      setSaving(false);
    }
  };

  // =========================================================
  // RESET
  // =========================================================

    const handleCancel = () => {
    setFormData({
      branchId: selectedBranchId || "",
      roleId: "",
      employeeId: "",
      userName: "",
      password: "",
      confirmPassword: "",
      expiryDate: "",
    });

    setPasswordMatchError(
      false
    );

    setRoleList([]);

    setAllEmployeeList([]);

    setEmployeeList([]);

    setStudentList([]);
  };

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <Container
      maxWidth="lg"
      sx={{
        my: 0,
      }}
    >
      <Box
        sx={{
          p: 4,
          borderRadius: 2,
          backgroundColor:
            theme.palette.background.paper,
        }}
      >
        {/* =====================================================
            HEADER
        ===================================================== */}

        <Box
          sx={{
            display: "flex",
            justifyContent:
              "space-between",
            alignItems: "center",
            mb: 3,
            gap: 2,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: "bold",
              color:
                theme.palette.primary.main,
              whiteSpace: "nowrap",
            }}
          >
            User Details
          </Typography>
        </Box>

        {/* =====================================================
            SUCCESS MODAL
        ===================================================== */}

        {showSuccess ? (
          <Modal
            open={showSuccess}
            onClose={() =>
              setShowSuccess(false)
            }
            BackdropProps={{
              style: {
                backgroundColor:
                  "rgba(0, 0, 0, 0.8)",
              },
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform:
                  "translate(-50%, -50%)",
                width: 400,
                bgcolor:
                  theme.palette.background.paper,
                borderRadius: 2,
                boxShadow: 24,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  backgroundColor:
                    theme.palette.primary.main,
                  padding:
                    "12px 16px",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color:
                      theme.palette.primary
                        .contrastText,
                  }}
                >
                  User Added ✅
                </Typography>
              </Box>

              <Box
                sx={{
                  padding: "20px",
                }}
              >
                <Typography
                  sx={{
                    mb: 2,
                  }}
                >
                  User details have
                  been saved successfully.
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent:
                      "flex-end",
                    mt: 3,
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      setShowSuccess(
                        false
                      );

                      navigate(-1);
                    }}
                  >
                    OK
                  </Button>
                </Box>
              </Box>
            </Box>
          </Modal>
        ) : (
                   <form
            onSubmit={
              handleSave
            }
          >
              <>
                {/* =================================================
                    ROLE + EMPLOYEE
                ================================================= */}

                {formData.branchId &&
                  (loading ? (
                    <Typography
                      align="center"
                      sx={{
                        py: 4,
                      }}
                    >
                      Loading...
                    </Typography>
                  ) : (
                    <>
                      {/* =================================================
                          USER ROLE
                      ================================================= */}

                      <FormControl
                        fullWidth
                        sx={{
                          mb: 3,
                        }}
                      >
                        <InputLabel
                          sx={{
                            color:
                              theme.palette
                                .text.primary,

                            fontWeight: 400,

                            "&.Mui-focused": {
                              color:
                                theme.palette
                                  .primary.main,

                              fontWeight: 500,
                            },
                          }}
                        >
                          User Role
                        </InputLabel>

                        <Select
                          name="roleId"
                          value={
                            formData.roleId ||
                            ""
                          }
                          label="User Role"
                          onChange={
                            handleChange
                          }
                        >
                          {roleList.length ===
                          0 ? (
                            <MenuItem
                              disabled
                            >
                              No roles available
                            </MenuItem>
                          ) : (
                            <MenuItem
                              value=""
                              disabled
                            >
                              Select Role
                            </MenuItem>
                          )}

                          {roleList.map(
                            (role) => (
                              <MenuItem
                                key={
                                  role.id
                                }
                                value={String(
                                  role.id
                                )}
                              >
                                {role.name ||
                                  role.userGroupName ||
                                  role.role_name ||
                                  role.roleName ||
                                  "Unknown"}
                              </MenuItem>
                            )
                          )}
                        </Select>
                      </FormControl>

                   {/* =================================================
    EMPLOYEE / STUDENT
================================================= */}

<FormControl
  fullWidth
  sx={{
    mb: 3,
  }}
>
  <InputLabel
    sx={{
      color: theme.palette.text.primary,
      fontWeight: 400,

      "&.Mui-focused": {
        color: theme.palette.primary.main,
        fontWeight: 500,
      },
    }}
  >
    {isStudentRole ? "Student Name" : "Employee Name"}
  </InputLabel>

  <Select
    name="employeeId"
    value={formData.employeeId || ""}
    label={isStudentRole ? "Student Name" : "Employee Name"}
    onChange={handleChange}
  >
    {/* ================= STUDENT ================= */}

    {isStudentRole ? (
      studentList.length === 0 ? (
        <MenuItem disabled value="">
          No students available
        </MenuItem>
      ) : (
        [
          <MenuItem
            key="student-placeholder"
            value=""
            disabled
          >
            Select Student
          </MenuItem>,

          ...studentList.map((student) => (
            <MenuItem
              key={`student-${student.id}`}
              value={String(student.id)}
            >
              {student.candidate_name ||
                student.name ||
                "Unknown"}
            </MenuItem>
          )),
        ]
      )
    ) : (
      /* ================= EMPLOYEE ================= */

      employeeList.length === 0 ? (
        <MenuItem disabled value="">
          No employees available for selected role
        </MenuItem>
      ) : (
        [
          <MenuItem
            key="employee-placeholder"
            value=""
            disabled
          >
            Select Employee
          </MenuItem>,

          ...employeeList.map((employee) => (
            <MenuItem
              key={`employee-${employee.id}`}
              value={String(employee.id)}
            >
              {employee.full_name ||
                employee.name ||
                `${employee.first_name || ""} ${
                  employee.last_name || ""
                }`.trim() ||
                employee.email ||
                "Unknown"}
            </MenuItem>
          )),
        ]
      )
    )}
  </Select>
</FormControl>
                    </>
                  ))}

                {/* =====================================================
                    USERNAME
                ===================================================== */}

                <TextField
                  label="User Name"
                  variant="outlined"
                  fullWidth
                  name="userName"
                  value={
                    formData.userName
                  }
                  onChange={
                    handleChange
                  }
                  sx={{
                    mb: 3,

                    "& .MuiInputLabel-root": {
                      color:
                        theme.palette.text.primary,
                    },

                    "& .MuiInputLabel-root.Mui-focused":
                      {
                        color:
                          theme.palette.primary.main,
                      },

                    "& .MuiOutlinedInput-notchedOutline":
                      {
                        borderColor:
                          theme.palette.text.primary,
                      },

                    "&:hover .MuiOutlinedInput-notchedOutline":
                      {
                        borderColor:
                          theme.palette.primary.main,
                      },
                  }}
                />

                {/* =====================================================
                    PASSWORD
                ===================================================== */}

                <TextField
                  label="Password"
                  type={
                    showPassword
                      ? "text"
                      : "password"
                  }
                  variant="outlined"
                  fullWidth
                  name="password"
                  value={
                    formData.password
                  }
                  onChange={
                    handleChange
                  }
                  error={
                    passwordMatchError
                  }
                  helperText={
                    passwordMatchError
                      ? "Passwords do not match"
                      : ""
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={
                            handleClickShowPassword
                          }
                          edge="end"
                        >
                          {showPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 3,

                    "& .MuiInputLabel-root": {
                      color:
                        theme.palette.text.primary,
                    },

                    "& .MuiInputLabel-root.Mui-focused":
                      {
                        color:
                          theme.palette.primary.main,
                      },
                  }}
                />

                {/* =====================================================
                    CONFIRM PASSWORD
                ===================================================== */}

                <TextField
                  label="Confirm Password"
                  type={
                    showConfirmPassword
                      ? "text"
                      : "password"
                  }
                  variant="outlined"
                  fullWidth
                  name="confirmPassword"
                  value={
                    formData.confirmPassword
                  }
                  onChange={
                    handleChange
                  }
                  error={
                    passwordMatchError
                  }
                  helperText={
                    passwordMatchError
                      ? "Passwords do not match"
                      : ""
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={
                            handleClickShowConfirmPassword
                          }
                          edge="end"
                        >
                          {showConfirmPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    mb: 3,

                    "& .MuiInputLabel-root": {
                      color:
                        theme.palette.text.primary,
                    },

                    "& .MuiInputLabel-root.Mui-focused":
                      {
                        color:
                          theme.palette.primary.main,
                      },
                  }}
                />

                {/* =====================================================
                    EXPIRY DATE
                ===================================================== */}

                <TextField
                  label="Expiry Date"
                  type="date"
                  variant="outlined"
                  fullWidth
                  name="expiryDate"
                  value={
                    formData.expiryDate
                  }
                  onChange={
                    handleChange
                  }
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    mb: 3,
                  }}
                />

                {/* =====================================================
                    BUTTONS
                ===================================================== */}

                <Box
                  sx={{
                    display: "flex",
                    justifyContent:
                      "flex-end",
                    gap: 2,
                  }}
                >
                  <Tooltip
                    title={
                      !formData.branchId
                        ? "Select Branch"
                        : !formData.roleId
                        ? "Select User Role"
                        : !formData.employeeId
                        ? isStudentRole
                          ? "Select Student Name"
                          : "Select Employee Name"
                        : !formData.userName
                        ? "Enter User Name"
                        : !formData.password
                        ? "Enter Password"
                        : !formData.confirmPassword
                        ? "Enter Confirm Password"
                        : !formData.expiryDate
                        ? "Select Expiry Date"
                        : ""
                    }
                    arrow
                  >
                    <span>
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={
                          saving ||
                          !formData.branchId ||
                          !formData.roleId ||
                          !formData.employeeId ||
                          !formData.userName ||
                          !formData.password ||
                          !formData.confirmPassword ||
                          !formData.expiryDate
                        }
                      >
                        {saving
                          ? "Saving..."
                          : "SAVE"}
                      </Button>
                    </span>
                  </Tooltip>

                  <Button
                    variant="outlined"
                    color="error"
                    onClick={
                      handleCancel
                    }
                  >
                    RESET
                  </Button>

                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() =>
                      navigate(-1)
                    }
                  >
                    BACK
                  </Button>
                </Box>
              </>
          </form>
        )}
      </Box>

      {/* =========================================================
          SNACKBAR
      ========================================================= */}

      <Snackbar
        open={
          openSnackbar
        }
        autoHideDuration={
          4000
        }
        onClose={() =>
          setOpenSnackbar(
            false
          )
        }
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Alert
          onClose={() =>
            setOpenSnackbar(
              false
            )
          }
          severity={
            snackbarSeverity
          }
          sx={{
            width: "100%",
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AddUser;